import {
  formatRetryAfter,
  isMetaRateLimitError,
  retryAfterSecondsFrom,
} from "@/lib/api/metaRateLimit";
import { metaCurrencyOffset } from "@/lib/ads/currency";

export interface ConnectedAdAccount {
  adAccountId: string;
  id: string;
  name: string;
  currency: string | null;
  balance: number | string | null;
  isActive: boolean;
  status: string | null;
  hasDataset: boolean;
  pageName: string | null;
  isDefault: boolean;
}

export interface AvailableAdAccount {
  adAccountId: string;
  id: string;
  name: string;
  currency: string | null;
  balance: number | null;
}

export interface RawConnectedAccountRow {
  ad_account_id?: string | null;
  ad_account_name?: string | null;
  currency?: string | null;
  balance?: number | string | null;
  status?: string | null;
  is_active?: boolean | null;
  has_dataset?: boolean | null;
  page_name?: string | null;
  is_default?: boolean | number | null;
}

export interface RawInventoryAccountRow {
  id?: string | null;
  account_id?: string | null;
  name?: string | null;
  currency?: string | null;
  balance?: number | string | null;
}

export function minorUnitsToMajor(value: number | string | null | undefined, currency?: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const raw = Number(value);
  return Number.isFinite(raw) ? raw / metaCurrencyOffset(currency) : null;
}

export const bareAdAccountId = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .replace(/^act_/i, "");

export const isReadOnlyAdAccount = (name: unknown): boolean =>
  String(name ?? "")
    .toLowerCase()
    .includes("read-only");

export const isDisconnectedAdAccount = (status: unknown): boolean =>
  String(status ?? "")
    .trim()
    .toLowerCase() === "disconnected";

export function normalizeConnectedAccounts(
  accounts: RawConnectedAccountRow[] | null | undefined,
  activeAccount: RawConnectedAccountRow | null | undefined,
): ConnectedAdAccount[] {
  const source =
    Array.isArray(accounts) && accounts.length
      ? accounts
      : activeAccount
        ? [{ ...activeAccount, is_active: true }]
        : [];

  const activeId = bareAdAccountId(activeAccount?.ad_account_id);

  return source
    .map((row) => {
      const adAccountId = bareAdAccountId(row?.ad_account_id);
      return {
        adAccountId,
        id: `act_${adAccountId}`,
        name: row?.ad_account_name || adAccountId,
        currency: row?.currency ?? null,
        balance: row?.balance ?? null,
        isActive:
          row?.is_active ??
          (row?.status ? row.status === "active" : Boolean(adAccountId) && adAccountId === activeId),
        status: row?.status ?? null,
        hasDataset: Boolean(row?.has_dataset),
        pageName: row?.page_name ?? null,
        isDefault: Boolean(row?.is_default),
      } as ConnectedAdAccount;
    })
    .filter(
      (acc) =>
        acc.adAccountId &&
        !isReadOnlyAdAccount(acc.name) &&
        !isDisconnectedAdAccount(acc.status),
    )
    .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name));
}

export function markSelectedAccounts(
  accounts: ConnectedAdAccount[],
  selectedId: string | null | undefined,
  defaultId?: string | null,
): ConnectedAdAccount[] {
  const selected = bareAdAccountId(selectedId);
  const fallbackDefault = bareAdAccountId(defaultId);
  const known = !!selected && accounts.some((acc) => acc.adAccountId === selected);
  return accounts
    .map((acc) => ({
      ...acc,
      isActive: known ? acc.adAccountId === selected : acc.isActive,
      isDefault: acc.isDefault || (!!fallbackDefault && acc.adAccountId === fallbackDefault),
    }))
    .sort(
      (a, b) =>
        Number(b.isActive) - Number(a.isActive) ||
        Number(b.isDefault) - Number(a.isDefault) ||
        a.name.localeCompare(b.name),
    );
}

export function availableToConnect(
  inventory: RawInventoryAccountRow[] | null | undefined,
  connected: ConnectedAdAccount[],
): AvailableAdAccount[] {
  const held = new Set(connected.map((a) => a.adAccountId));

  return (Array.isArray(inventory) ? inventory : [])
    .map((row) => {
      const adAccountId = bareAdAccountId(row?.id ?? row?.account_id);
      return {
        adAccountId,
        id: `act_${adAccountId}`,
        name: row?.name || adAccountId,
        currency: row?.currency ?? null,
        balance: minorUnitsToMajor(row?.balance, row?.currency),
      } as AvailableAdAccount;
    })
    .filter(
      (acc) => acc.adAccountId && !held.has(acc.adAccountId) && !isReadOnlyAdAccount(acc.name),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type AdAccountInventoryState =
  | "loading"
  | "failed"
  | "empty"
  | "all-connected"
  | "ready";

export function classifyInventoryState(input: {
  isPending: boolean;
  isError: boolean;
  data?: {
    ad_accounts_available?: boolean;
    ad_accounts_connected_elsewhere?: number;
  } | null;
  availableCount: number;
  inventoryCount: number;
}): AdAccountInventoryState {
  if (input.isPending) return "loading";
  if (input.isError || input.data === null || input.data === undefined) return "failed";
  if (input.data.ad_accounts_available === false) return "failed";
  if (input.availableCount > 0) return "ready";
  if (input.inventoryCount === 0) {
    return (input.data.ad_accounts_connected_elsewhere ?? 0) > 0 ? "all-connected" : "empty";
  }
  return "all-connected";
}

export function filterAccountsBySearch<T extends { name: string; id: string }>(
  accounts: T[],
  search: string,
): T[] {
  const term = search.trim().toLowerCase();
  if (!term) return accounts;
  return accounts.filter(
    (acc) =>
      acc.name.toLowerCase().includes(term) || acc.id.toLowerCase().includes(term),
  );
}

export function formatAccountBalance(
  balance: number | string | null | undefined,
  currency: string | null | undefined,
): string {
  const symbol = currency === "INR" ? "₹" : "$";
  const raw = Number(balance);
  const amount = balance === null || balance === undefined || balance === "" || !Number.isFinite(raw)
    ? 0
    : raw;
  return `${symbol}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function buildSwitchPayload(account: {
  adAccountId: string;
  name?: string | null;
  currency?: string | null;
}): { ad_account_id: string; ad_account_name?: string; currency?: string } {
  const payload: { ad_account_id: string; ad_account_name?: string; currency?: string } = {
    ad_account_id: bareAdAccountId(account.adAccountId),
  };
  if (account.name) payload.ad_account_name = account.name;
  if (account.currency) payload.currency = account.currency;
  return payload;
}

export function adAccountLimitMessage(error: unknown): string | null {
  const err = error as
    | {
        code?: string;
        meta?: { code?: string; limit?: number | null; used?: number | null };
        message?: string;
        error?: string;
      }
    | null
    | undefined;
  const code = err?.code || err?.meta?.code;
  if (code === "AD_ACCOUNT_LIMIT_UNAVAILABLE") {
    return err?.message || err?.error || AD_ACCOUNT_LIMIT_UNAVAILABLE_MESSAGE;
  }
  if (code !== "AD_ACCOUNT_LIMIT_REACHED") return null;
  if (err?.meta?.limit === 0) {
    return err.message || err.error || `${AD_ACCOUNTS_NOT_IN_PLAN} Ask your admin to upgrade.`;
  }
  return (
    err?.message ||
    err?.error ||
    "Your plan does not include another Meta ad account. Disconnect one, or upgrade to connect more."
  );
}

export const AD_ACCOUNT_DISCONNECT_ADMIN_ONLY =
  "Only workspace admins can disconnect an ad account.";

export const AD_EXPERIMENT_END_ADMIN_ONLY = "Only workspace admins can end ad experiments.";

export const AD_EXPERIMENT_START_ADMIN_ONLY = "Only workspace admins can start ad experiments.";

export const AD_ACCOUNTS_NOT_IN_PLAN = "Meta Ads accounts are not included in your plan.";

export const AD_ACCOUNT_LIMIT_UNAVAILABLE_MESSAGE =
  "We couldn't verify your plan's ad-account limit. Please try again.";

export interface AdAccountLimit {
  limit: number | null;
  used: number;
  unlimited: boolean;
}

export function isAdAccountLimitReached(limit: AdAccountLimit | null | undefined): boolean {
  if (!limit || limit.unlimited) return false;
  if (typeof limit.limit !== "number" || !Number.isFinite(limit.limit)) return false;
  return Number(limit.used ?? 0) >= limit.limit;
}

export function adAccountLimitNotice(
  limit: AdAccountLimit | null | undefined,
  opts: { canDisconnect: boolean },
): string | null {
  if (!limit || !isAdAccountLimitReached(limit)) return null;
  if (limit.limit === 0) {
    return opts.canDisconnect
      ? AD_ACCOUNTS_NOT_IN_PLAN
      : `${AD_ACCOUNTS_NOT_IN_PLAN} Ask your admin to upgrade.`;
  }
  const max = limit.limit as number;
  const base =
    max === 1
      ? "Your plan includes 1 Meta ad account, and it is already connected."
      : `Your plan includes ${max} Meta ad accounts, and all of them are connected.`;
  return opts.canDisconnect
    ? `${base} Disconnect one, or upgrade to connect more.`
    : `${base} Ask your admin to upgrade, or to disconnect one you no longer use.`;
}

export interface DisconnectPreviewCampaign {
  id: string;
  name: string;
  effective_status: string | null;
}

export interface DisconnectPreview {
  ad_account_id: string;
  ad_account_name: string | null;
  active_campaigns: DisconnectPreviewCampaign[];
  active_campaigns_count: number;
  lookup_failed: boolean;
  lookup_error?: string | null;
  would_promote_ad_account_id?: string | null;
  would_promote_ad_account_name?: string | null;
}

export interface DisconnectResult {
  disconnected?: boolean;
  was_active?: boolean;
  disconnected_ad_account_id?: string | null;
  disconnected_ad_account_name?: string | null;
  promoted_ad_account_id?: string | null;
  promoted_ad_account_name?: string | null;
  still_connected?: boolean;
  already_disconnected?: boolean;
  pause_requested?: boolean;
  paused?: Array<{ id: string; name?: string | null }>;
  pause_failed?: Array<{ id: string; name?: string | null; error?: string | null }>;
  pause_lookup_failed?: boolean;
  pause_lookup_error?: string | null;
}

export const PAUSE_LOOKUP_FAILED_CODE = "PAUSE_LOOKUP_FAILED";

export const PAUSE_LOOKUP_FAILED_MESSAGE =
  "We couldn't reach Meta to pause this account's campaigns, so nothing was disconnected. Try again, or choose to leave them running.";

export function disconnectBlockedMessage(
  error: unknown,
  pauseCampaigns: boolean | undefined,
): string | null {
  if (!error || typeof error !== "object") return null;
  const err = error as {
    code?: unknown;
    meta?: { code?: unknown } | null;
    message?: unknown;
    error?: unknown;
  };
  const code = err.code ?? err.meta?.code;
  if (code === PAUSE_LOOKUP_FAILED_CODE) {
    const raw = [err.message, err.error].find(
      (m): m is string => typeof m === "string" && !!m.trim() && !/^HTTP \d+$/.test(m.trim()),
    );
    return raw ? raw.trim() : PAUSE_LOOKUP_FAILED_MESSAGE;
  }
  if (pauseCampaigns !== true || !isMetaRateLimitError(error)) return null;
  const wait = formatRetryAfter(retryAfterSecondsFrom(error));
  return `Meta is limiting requests for this workspace right now, so we couldn't pause this account's campaigns and nothing was disconnected. ${
    wait ? `Try again in ${wait}` : "Try again shortly"
  }, or choose to leave them running.`;
}

export function formatCampaignStatus(status: string | null | undefined): string {
  const words = String(status ?? "")
    .replace(/_/g, " ")
    .trim()
    .toLowerCase();
  if (!words) return "Active";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export function summarizeDisconnectResult(
  result: DisconnectResult | null | undefined,
  opts: { nowUsing?: string | null } = {},
): {
  title: string;
  description?: string;
  variant: "success" | "warning";
} {
  const r = result ?? {};
  const removedLabel = r.disconnected_ad_account_name || r.disconnected_ad_account_id;
  const promotedLabel = r.promoted_ad_account_name || r.promoted_ad_account_id;

  if (r.already_disconnected) {
    return {
      title: removedLabel ? `${removedLabel} was already disconnected` : "Already disconnected",
      variant: "success",
    };
  }

  const paused = Array.isArray(r.paused) ? r.paused : [];
  const failed = Array.isArray(r.pause_failed) ? r.pause_failed : [];
  const parts: string[] = [];

  if (paused.length > 0) {
    parts.push(
      `Paused ${paused.length} ${plural(paused.length, "campaign", "campaigns")} on Meta.`,
    );
  }
  if (failed.length > 0) {
    const shown = failed.slice(0, 3).map((f) => {
      const label = f.name || f.id;
      return f.error ? `${label} (${f.error})` : label;
    });
    const more = failed.length - shown.length;
    parts.push(
      `Couldn't pause ${failed.length} ${plural(failed.length, "campaign", "campaigns")}, so ${plural(
        failed.length,
        "it is",
        "they are",
      )} still running on Meta: ${shown.join(", ")}${more > 0 ? `, +${more} more` : ""}. Pause ${plural(
        failed.length,
        "it",
        "them",
      )} in Meta Ads Manager.`,
    );
  }
  const lookupFailed = r.pause_lookup_failed === true;
  if (lookupFailed) {
    parts.push(
      "We couldn't reach Meta to pause this account's campaigns, so none were paused and any running ads keep spending. Pause them in Meta Ads Manager.",
    );
  }
  const provedNothingActive =
    r.pause_requested === true &&
    !lookupFailed &&
    Array.isArray(r.paused) &&
    Array.isArray(r.pause_failed) &&
    paused.length === 0 &&
    failed.length === 0;
  if (provedNothingActive) {
    parts.push("There were no active campaigns to pause.");
  }
  if (promotedLabel) parts.push(`Now using ${promotedLabel}.`);
  else if (r.disconnected && r.still_connected === false) parts.push("No ad account is connected now.");
  else if (opts.nowUsing) parts.push(`Now using ${opts.nowUsing}.`);

  return {
    title: removedLabel ? `Disconnected ${removedLabel}` : "Ad account disconnected",
    ...(parts.length ? { description: parts.join(" ") } : {}),
    variant: failed.length > 0 || lookupFailed ? "warning" : "success",
  };
}

export function leadRecoverySummary(result: unknown): {
  title: string;
  description: string;
  variant: "success" | "info";
} {
  const synced = Number((result as { synced?: unknown } | null | undefined)?.synced);
  if (!Number.isFinite(synced)) {
    return {
      title: "Missed leads synced",
      description: "Leads from while this ad account was disconnected have been synced.",
      variant: "success",
    };
  }
  const n = synced > 0 ? Math.floor(synced) : 0;
  if (n === 0) {
    return {
      title: "No missed leads",
      description: "No leads came in while this ad account was disconnected.",
      variant: "info",
    };
  }
  return {
    title: "Missed leads recovered",
    description: `Recovered ${n} ${plural(n, "lead", "leads")} from while it was disconnected.`,
    variant: "success",
  };
}

export type MetaAdsOAuthPurpose = "add_accounts";

export type LoginAdAccountState = "available" | "reconnectable" | "connected" | "connected_elsewhere";

export interface LoginAdAccount {
  ad_account_id: string;
  name: string | null;
  currency: string | null;
  account_status: number | null;
  state: LoginAdAccountState;
}

export interface LoginAdAccountsResult {
  login: string;
  ad_accounts: LoginAdAccount[];
  incomplete: boolean;
  lost_access: Array<{ ad_account_id: string; name: string | null }>;
  max_per_connect: number;
  account_limit: AdAccountLimit | null;
}

export const isChoosableLoginAccount = (acc: LoginAdAccount) =>
  acc.state === "available" || acc.state === "reconnectable";

export function loginPickLimit(result: Pick<LoginAdAccountsResult, "max_per_connect" | "account_limit">): number {
  const perConnect = Number(result.max_per_connect) > 0 ? Math.floor(Number(result.max_per_connect)) : 20;
  const limit = result.account_limit;
  if (!limit || limit.unlimited || typeof limit.limit !== "number" || !Number.isFinite(limit.limit)) return perConnect;
  return Math.max(0, Math.min(perConnect, limit.limit - Number(limit.used ?? 0)));
}

export type BusinessLoginOutcome =
  | "connected"
  | "reconnected"
  | "already_connected"
  | "connected_elsewhere"
  | "limit_reached"
  | "single_account_only"
  | "unavailable"
  | "rate_limited"
  | "failed";

export interface BusinessLoginConnectResult {
  selected_count: number;
  results: Array<{
    ad_account_id: string;
    name: string | null;
    outcome: BusinessLoginOutcome;
    message?: string;
    lead_recovery_recommended?: boolean;
  }>;
}

export interface BusinessLoginSummary {
  tone: "success" | "warning" | "error";
  headline: string;
  lines: string[];
}

export const accountNames = (rows: Array<{ ad_account_id: string; name: string | null }>) =>
  rows.map((r) => r.name || `act_${r.ad_account_id}`).join(", ");

export function summarizeBusinessLoginResult(result: BusinessLoginConnectResult): BusinessLoginSummary {
  const results = Array.isArray(result?.results) ? result.results : [];
  const by = (...outcomes: BusinessLoginOutcome[]) => results.filter((r) => outcomes.includes(r.outcome));

  const connected = by("connected", "reconnected");
  const lines: string[] = [];

  const already = by("already_connected");
  if (already.length) lines.push(`Already connected: ${accountNames(already)}.`);
  const elsewhere = by("connected_elsewhere");
  if (elsewhere.length) {
    lines.push(`Connected in another workspace, disconnect it there first: ${accountNames(elsewhere)}.`);
  }
  const limited = by("limit_reached");
  if (limited.length) {
    lines.push(
      `${limited[0].message || "Your plan's ad-account limit is reached."} Not connected: ${accountNames(limited)}.`,
    );
  }
  const single = by("single_account_only");
  if (single.length) {
    lines.push("This workspace can hold one ad account at a time. Choose just one and connect again.");
  }
  const unavailable = by("unavailable");
  if (unavailable.length) lines.push(`Facebook didn't return these accounts: ${accountNames(unavailable)}.`);
  const rateLimited = by("rate_limited");
  if (rateLimited.length) {
    lines.push(`Meta is limiting requests right now. Try again in a few minutes for: ${accountNames(rateLimited)}.`);
  }
  const failed = by("failed");
  if (failed.length) lines.push(`Couldn't connect: ${accountNames(failed)}.${failed[0].message ? ` ${failed[0].message}` : ""}`);

  if (connected.length) {
    return {
      tone: lines.length ? "warning" : "success",
      headline: `Connected ${connected.length} ${plural(connected.length, "ad account", "ad accounts")}: ${accountNames(connected)}`,
      lines,
    };
  }
  const nothingNew = already.length === results.length && results.length > 0;
  return {
    tone: nothingNew ? "success" : failed.length || rateLimited.length ? "error" : "warning",
    headline: nothingNew ? "Everything you chose is already connected" : "No new ad accounts were connected",
    lines,
  };
}
